import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Button,
  ActionRow,
  useToggle,
} from '@openedx/paragon';
import {
  getSelectedCompanyDetails, getCompanyCatalog, getLegacyLicenses, getPrograms, getFullCourseList,
  getCompanyEmployeePagesForOrg, getCompanyEmployeesForOrg,
} from '../../../data/selectors';
import { LearnerOption } from './LearnerSearchDropdown';
import CreateLearnersModal from './CreateLearnersModal';
import EnrollLearnersModal from './EnrollLearnersModal';
import UpdateLearnersModal from './UpdateLearnersModal';
import RemoveLearnersModal from './RemoveLearnersModal';
import { getActiveVersionCourseId, getCatalogRemainingAvailability } from './catalogUtils';

interface LicenseOption {
  id: string;
  name: string;
  type: 'legacy_bundle' | 'catalog_course' | 'program';
  available: number | null;
  used: number;
  disabled: boolean;
  pk_id?: number;
  course_list?: string[]; // For legacy bundles - contains course IDs
  displayName?: string; // Resolved human-readable course name (catalog_course only)
  courseIds?: string[]; // Course IDs contained in this program (program type only), used for search
}

const ManageLearners: React.FC = () => {
  // Redux dispatch and selectors
  const dispatch = useDispatch();

  // Modal states
  const [isCreateModalOpen, openCreateModal, closeCreateModal] = useToggle(false);
  const [isEnrollModalOpen, openEnrollModal, closeEnrollModal] = useToggle(false);
  const [isUpdateModalOpen, openUpdateModal, closeUpdateModal] = useToggle(false);
  const [isRemoveModalOpen, openRemoveModal, closeRemoveModal] = useToggle(false);

  // Redux data
  const selectedCompany = useSelector(getSelectedCompanyDetails);
  const companyCatalog = useSelector(getCompanyCatalog);
  const legacyLicenses = useSelector(getLegacyLicenses);
  const programs = useSelector(getPrograms);
  const courses = useSelector(getFullCourseList);
  const employeePages = useSelector(
    (state: any) => getCompanyEmployeePagesForOrg(state, selectedCompany?.company_name),
  );
  const companyEmployees = useSelector(
    (state: any) => getCompanyEmployeesForOrg(state, selectedCompany?.company_name),
  );
  const rosterIncomplete = Boolean(
    employeePages && companyEmployees.length < employeePages.count,
  );
  // Use every employee page loaded by the background roster fetch, not only the visible page.
  const learners = useMemo<LearnerOption[]>(() => {
    const seen = new Set<string>();
    return (Array.isArray(companyEmployees) ? companyEmployees : [])
      .filter((row: any) => {
        if (!row.username || seen.has(row.username)) {
          return false;
        }
        seen.add(row.username);
        return true;
      })
      .map((row: any) => ({
        name: row.name || '',
        email: row.email || '',
        username: row.username,
      }));
  }, [companyEmployees]);

  // Resolve a course ID to its human-readable display name
  const getCourseDisplayName = (courseId: string): string | null => {
    const course = courses?.find((c: any) => c.courseId === courseId);
    return course?.displayName || null;
  };

  // Combine data from split sources
  const selectedCompanyLicenses = React.useMemo(() => ({
    legacyBundles: legacyLicenses?.bundles || [],
    catalog: companyCatalog?.catalog || {},
    programs: programs || [],
  }), [companyCatalog, legacyLicenses, programs]);

  // License allocation tracking (how many of each license allocated across all learners)
  const [licenseAllocation, setLicenseAllocation] = useState<Record<string, number>>({});

  // Process licenses into dropdown options
  const licenseOptions = useMemo(() => {
    if (!selectedCompanyLicenses) { return []; }

    const options: LicenseOption[] = [];
    const { legacyBundles = [], catalog = {}, programs: programList = [] } = selectedCompanyLicenses;

    // When hide_skills_if_only_pathways is false (or unset), show skills both standalone
    // and inside pathways so staff can enroll in individual courses even when a pathway exists.
    // When true, only show the pathway-level entry (hide the duplicate standalone entries).
    const hideSkillsIfOnlyPathways = companyCatalog?.options?.hide_skills_if_only_pathways ?? false;

    // Add legacy bundles
    legacyBundles.forEach((bundle: any) => {
      const allocated = licenseAllocation[bundle.license] || 0;
      const remaining = bundle.available === null ? null : bundle.available - bundle.used - allocated;

      options.push({
        id: bundle.license,
        name: `${bundle.license} (Legacy Bundle)`,
        type: 'legacy_bundle',
        available: bundle.available,
        used: bundle.used,
        disabled: remaining !== null && remaining <= 0,
        course_list: bundle.course_list || [],
      });
    });

    // Build the set of courses that belong to at least one program
    const coursesInPrograms = new Set(
      programList.flatMap((p: any) => p.course_list || []),
    );

    // Add catalog courses.
    // Skip pathway entries (type="pathway") — those are handled in the programs section.
    // If hideSkillsIfOnlyPathways is true, skip courses that are already covered by a program.
    // Otherwise show all courses, including those in programs, so staff can enroll individually.
    Object.keys(catalog).forEach((courseId: string) => {
      if (catalog[courseId]?.type === 'pathway') {
        return; // pathway entry — handled below
      }
      if (hideSkillsIfOnlyPathways && coursesInPrograms.has(courseId)) {
        return; // hidden per company flag
      }
      const course = catalog[courseId];
      const activeVersionCourseId = getActiveVersionCourseId(courseId, course);
      const allocated = licenseAllocation[activeVersionCourseId] || 0;
      const remaining = getCatalogRemainingAvailability(course.available, allocated);
      const displayName = getCourseDisplayName(activeVersionCourseId);

      options.push({
        id: activeVersionCourseId,
        // Show "Display Name | course-v1:ORG+... (Course)" when a display name is available
        name: displayName ? `${displayName} | ${activeVersionCourseId} (Course)` : `${activeVersionCourseId} (Course)`,
        type: 'catalog_course',
        available: course.available,
        used: course.used || 0,
        disabled: remaining !== null && remaining <= 0,
        pk_id: course.id,
        displayName: displayName || undefined,
      });
    });

    // Add programs/pathways.
    // In v2 the catalog has a pathway entry keyed by the program's item_id (UUID).
    // Read availability directly from that entry rather than summing per-course seats.
    programList.forEach((program: any) => {
      if (program.course_list && program.course_list.length > 0) {
        const catalogEntry = catalog[program.item_id];
        const available = catalogEntry?.available ?? null;
        const used = catalogEntry?.used ?? 0;
        const allocated = licenseAllocation[program.item_id] || 0;
        const remaining = getCatalogRemainingAvailability(available, allocated);

        options.push({
          id: program.item_id,
          name: `${program.name} (Pathway)`,
          type: 'program',
          available,
          used,
          disabled: remaining !== null && remaining <= 0,
          pk_id: catalogEntry?.id,
          courseIds: program.course_list || [], // used for search — lets modal find pathway by any contained skill
        });
      }
    });

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCompanyLicenses, licenseAllocation, courses, companyCatalog?.options?.hide_skills_if_only_pathways]);

  return (
    <Card className="manage-learners-card">
      <Card.Body className="px-4">
        <div className="manage-learners-buttons pt-3 mb-3">
          <ActionRow>
            <Button variant="primary" onClick={openCreateModal}>
              Add/Create/Enroll New Learners
            </Button>
            <Button variant="primary" onClick={openEnrollModal}>
              Enroll Existing Learners
            </Button>
            <Button variant="primary" onClick={openUpdateModal}>
              Update Learner Info
            </Button>
            <Button variant="primary" onClick={openRemoveModal}>
              Remove Learners from Org
            </Button>
          </ActionRow>
        </div>

        {/* Create New Learners Modal */}
        <CreateLearnersModal
          isOpen={isCreateModalOpen}
          onClose={closeCreateModal}
          selectedCompany={selectedCompany}
          licenseOptions={licenseOptions}
          licenseAllocation={licenseAllocation}
          onLicenseAllocationChange={setLicenseAllocation}
        />

        {/* Enroll Learners Modal */}
        <EnrollLearnersModal
          isOpen={isEnrollModalOpen}
          onClose={closeEnrollModal}
          selectedCompany={selectedCompany}
          licenseOptions={licenseOptions}
          licenseAllocation={licenseAllocation}
          onLicenseAllocationChange={setLicenseAllocation}
          learners={learners}
          companyName={selectedCompany?.company_name}
          rosterIncomplete={rosterIncomplete}
        />

        {/* Update Learners Modal */}
        <UpdateLearnersModal
          isOpen={isUpdateModalOpen}
          onClose={closeUpdateModal}
          learners={learners}
          selectedCompany={selectedCompany}
          rosterIncomplete={rosterIncomplete}
        />

        {/* Remove Learners Modal */}
        <RemoveLearnersModal
          isOpen={isRemoveModalOpen}
          onClose={closeRemoveModal}
          selectedCompany={selectedCompany}
          learners={learners}
          dispatch={dispatch}
          rosterIncomplete={rosterIncomplete}
        />
      </Card.Body>
    </Card>
  );
};

export default ManageLearners;
